import axios from 'axios';
import { useEffect, useState } from 'react';
import { logOut, updateUserPassword } from '../auth/Auth';
import EyeIcon from '../Elements/EyeIcon';
import Snackbar from './PasswordSnackBar';
import SaveButton from '../Elements/SaveButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome } from '@fortawesome/free-solid-svg-icons';

interface PasswordProps {
    activeTabExit: () => void;
}

function Password({ activeTabExit }: PasswordProps) {
    const [userName, setUserName] = useState("");
    const [open, setOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const fetchUserName = async () => {
            try {
                const response = await axios.get('/pool/name', { responseType: 'text' });
                setUserName(response.data);
            } catch (error) {
                console.error('Error fetching username:', error);
            }
        };

        fetchUserName();
    }, []);

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        resetForm();
    };

    const resetForm = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setError("");
        setSuccessMessage("");
    };

    const handleChangePassword = async () => {
        setIsLoading(true)
        if (newPassword !== confirmPassword) {
            setError("New passwords do not match");
            setIsLoading(false)
            return;
        }
        try {
            await updateUserPassword(currentPassword, newPassword);
            setSuccessMessage("Password change successful. Please relogin.");
            setTimeout(() => {
                logOut(() => {
                    console.log("User logged out after password change");
                    handleClose();
                });
            }, 2000);
            setIsLoading(false)
        } catch (err) {
            console.error("Error changing password:", err);
            setError("Current password is incorrect or could not be updated.");
            setIsLoading(false)
        }
    };

    const handleCloseSnackbar = () => {
        setError('');
        setSuccessMessage('')
    };

    return (
        <div className="flex flex-col items-center py-4 md:py-8">
            <div className="w-full max-w-xl backdrop-blur-xl transition-all">
                <div className="mb-8 text-center md:text-left">
                    <h2 className="text-xl md:text-2xl font-black text-[#0A3A6B] dark:text-blue-400 tracking-tight">Change Password</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-1">Update your security credentials</p>
                </div>

                <div className="space-y-6">
                    <div className="w-full">
                        <label className="block text-slate-700 dark:text-slate-300 text-xs font-bold mb-2 uppercase tracking-widest ml-1" htmlFor="current-password">
                            Current Password
                        </label>
                        <div className="relative group">
                            <input
                                id="current-password"
                                type={showCurrentPassword ? 'text' : 'password'}
                                className="w-full border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white rounded-2xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all group-hover:border-slate-300 dark:group-hover:border-slate-600 shadow-inner"
                                placeholder="••••••••••••"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                            />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                <EyeIcon isVisible={showCurrentPassword} onClick={() => setShowCurrentPassword(!showCurrentPassword)} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full">
                        <label className="block text-slate-700 dark:text-slate-300 text-xs font-bold mb-2 uppercase tracking-widest ml-1" htmlFor="new-password">
                            New Password
                        </label>
                        <div className="relative group">
                            <input
                                id="new-password"
                                type={showNewPassword ? 'text' : 'password'}
                                className="w-full border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white rounded-2xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all group-hover:border-slate-300 dark:group-hover:border-slate-600 shadow-inner"
                                placeholder="••••••••••••"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                <EyeIcon isVisible={showNewPassword} onClick={() => setShowNewPassword(!showNewPassword)} />
                            </div>
                        </div>
                    </div>

                    <div className="w-full">
                        <label className="block text-slate-700 dark:text-slate-300 text-xs font-bold mb-2 uppercase tracking-widest ml-1" htmlFor="confirm-password">
                            Confirm New Password
                        </label>
                        <div className="relative group">
                            <input
                                id="confirm-password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                className="w-full border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white rounded-2xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all group-hover:border-slate-300 dark:group-hover:border-slate-600 shadow-inner"
                                placeholder="••••••••••••"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                <EyeIcon isVisible={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                            </div>
                        </div>
                    </div>

                    <div className='flex flex-col sm:flex-row justify-end items-center gap-3 pt-6'>
                        <button
                            type="button"
                            onClick={activeTabExit}
                            className="w-full sm:w-auto px-8 py-3 text-sm font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50 rounded-full hover:bg-slate-200 dark:hover:bg-slate-600 transition-all active:scale-95 border border-slate-200 dark:border-slate-700"
                        >
                            Cancel
                        </button>
                        <SaveButton
                            isLoading={isLoading}
                            onClick={handleChangePassword}
                        >
                            Update Password
                        </SaveButton>
                    </div>
                </div>
            </div>

            <Snackbar
                error={error}
                successMessage={successMessage}
                onClose={handleCloseSnackbar}
            />
        </div>
    );
}

export default Password;
